import { cx } from "@emotion/css";
import { CloudUpload } from "lucide-react";
import { type DragEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFontSize } from "src/hooks/useFontSize";

interface ImportDropzoneProps {
	accept: string;
	multiple: boolean;
	formats: string[];
	onFiles: (files: File[]) => void;
	// Własna akcja kliknięcia zamiast systemowego wyboru pliku (np. galeria Capacitor).
	onPick?: () => void;
}

export const ImportDropzone = ({
	accept,
	multiple,
	formats,
	onFiles,
	onPick,
}: ImportDropzoneProps) => {
	const { t } = useTranslation();
	const { modalTextFontSizeClass } = useFontSize();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragOver, setIsDragOver] = useState(false);

	const handleDrop = (event: DragEvent) => {
		event.preventDefault();
		setIsDragOver(false);
		onFiles(Array.from(event.dataTransfer.files));
	};

	return (
		<>
			<button
				type="button"
				className={cx(
					"upload--file upload--file--tinted button-reset w-100 px-24",
					isDragOver && "upload--file--active",
				)}
				onClick={() => (onPick ? onPick() : inputRef.current?.click())}
				onDragOver={(event) => {
					event.preventDefault();
					setIsDragOver(true);
				}}
				onDragLeave={() => setIsDragOver(false)}
				onDrop={handleDrop}
				data-test-id="import-dropzone"
			>
				<span className="flex-center-row bg-white border-light-grey border-radius-16 p-16">
					<CloudUpload className="icon-normal icon-primary" />
				</span>
				<span className={cx(modalTextFontSizeClass, "text-black")}>
					{t("flashcardsImport.dropzone.drop", "Przeciągnij plik tutaj lub")}{" "}
					<span className="text-primary text-underline">
						{t("flashcardsImport.dropzone.pick", "wybierz z dysku")}
					</span>
				</span>
				<span className="flex gap-8 flex-wrap justify-center">
					{formats.map((format) => (
						<span key={format} className="chip">
							{format}
						</span>
					))}
				</span>
			</button>
			<input
				ref={inputRef}
				type="file"
				accept={accept}
				multiple={multiple}
				hidden
				onChange={(event) => {
					const files = Array.from(event.target.files ?? []);
					event.target.value = "";
					onFiles(files);
				}}
				data-test-id="import-file-input"
			/>
		</>
	);
};
